import { useEffect, useRef, useState, useCallback } from 'react';
import { useMeetingSelector } from '../meeting/meetingProviderContextDef';

const NOOP = () => {};

const selectMeeting = s => s.meeting;
const selectIsMeetingJoined = s => s.isMeetingJoined;

/**
 *
 * @param {string} topic
 * @param {{
 *  onMessageReceived?: Function,
 *  onOldMessagesReceived?: Function,
 * }} options
 * @returns {{
 *  publish: (message: string,{persist: boolean, sendOnly: Array<String>}, payload: object) => Promise<void>
 *  messages: Array<{
 *    id: string,
 *    message: string,
 *    senderId: string,
 *    senderName: string,
 *    timestamp: string,
 *    topic: string,
 *    payload: object,
 *  }>
 * }}
 */
const usePubSub = (
  topic,
  { onMessageReceived = NOOP, onOldMessagesReceived = NOOP } = {}
) => {
  const meeting = useMeetingSelector(selectMeeting);
  const isMeetingJoined = useMeetingSelector(selectIsMeetingJoined);

  const [messages, setMessages] = useState([]);
  const isSubscribed = useRef(false);

  const onMessageReceivedRef = useRef(onMessageReceived);
  const onOldMessagesReceivedRef = useRef(onOldMessagesReceived);
  const meetingRef = useRef(meeting);

  onMessageReceivedRef.current = onMessageReceived;
  onOldMessagesReceivedRef.current = onOldMessagesReceived;
  meetingRef.current = meeting;

  /**
   *
   * @param {string} message
   * @param {{persist: boolean}} options
   * @param {object} payload
   * @param {Array<String>} sendOnly
   */
  const publish = useCallback(
    async (message, options, payload) => {
      if (meetingRef.current) {
        return await meetingRef.current.pubSub.publish(
          topic,
          message,
          options,
          payload
        );
      }
    },
    [topic]
  );

  const _handlePubSub = useCallback(data => {
    setMessages(s => [...s, data]);
    onMessageReceivedRef.current(data);
  }, []);

  useEffect(() => {
    if (!meeting || isSubscribed.current || !isMeetingJoined) return;

    isSubscribed.current = true;

    const subscribeToPubsub = async () => {
      const data = await meeting?.pubSub?.subscribe(topic, _handlePubSub);
      if (data) {
        onOldMessagesReceivedRef.current(data.messages);
        setMessages(data.messages);
      }
    };

    subscribeToPubsub();

    return () => {
      isSubscribed.current = false;
      meetingRef.current?.pubSub?.unsubscribe(topic, _handlePubSub);
    };
  }, [meeting, isMeetingJoined, topic, _handlePubSub]);

  return { publish, messages };
};

export default usePubSub;
